import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { FiPlus, FiSearch, FiRefreshCw } from 'react-icons/fi';
import useAdminStore from '../../stores/useAdminStore';
import AdminStatisticsCards from '../../components/admin/AdminStatisticsCards';
import EstablishmentTable from '../../components/establishments/EstablishmentTable';
import EstablishmentForm from '../../components/establishments/EstablishmentForm';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const PlatformAdminDashboard = () => {
  const { isMobile, addNotification } = useApp();
  const {
    platformStats,
    establishments,
    isLoadingStats,
    isLoadingEstablishments,
    fetchPlatformStats,
    fetchEstablishments,
    createEstablishment,
    updateEstablishment,
    deleteEstablishment
  } = useAdminStore();

  const [showEstablishmentForm, setShowEstablishmentForm] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([
        fetchPlatformStats(),
        fetchEstablishments()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los datos del dashboard'
      });
    }
  };

  const handleRefresh = async () => {
    await loadData();
    addNotification({
      type: 'success',
      message: 'Datos actualizados correctamente'
    });
  };

  const handleCreateEstablishment = () => {
    setSelectedEstablishment(null);
    setShowEstablishmentForm(true);
  };

  const handleEditEstablishment = (establishment) => {
    setSelectedEstablishment(establishment);
    setShowEstablishmentForm(true);
  };

  const handleDeleteEstablishment = (establishment) => {
    setDeleteConfirm(establishment);
  };

  const confirmDeleteEstablishment = async () => {
    try {
      await deleteEstablishment(deleteConfirm.id);
      setDeleteConfirm(null);

      addNotification({
        type: 'success',
        message: 'Establecimiento eliminado exitosamente'
      });
    } catch (error) {
      console.error('Error deleting establishment:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar el establecimiento'
      });
    }
  };

  const handleEstablishmentSaved = async (establishmentData) => {
    try {
      if (selectedEstablishment) {
        await updateEstablishment(selectedEstablishment.id, establishmentData);
        addNotification({
          type: 'success',
          message: 'Establecimiento actualizado exitosamente'
        });
      } else {
        await createEstablishment(establishmentData);
        addNotification({
          type: 'success',
          message: 'Establecimiento creado exitosamente'
        });
      }

      setShowEstablishmentForm(false);
      setSelectedEstablishment(null);
    } catch (error) {
      console.error('Error saving establishment:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar el establecimiento'
      });
    }
  };

  const getFilteredEstablishments = () => {
    return establishments.filter(establishment => {
      const matchesSearch = !searchTerm ||
        establishment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        establishment.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        establishment.city?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !statusFilter ||
        (statusFilter === 'active' && establishment.is_active) ||
        (statusFilter === 'inactive' && !establishment.is_active);

      return matchesSearch && matchesStatus;
    });
  };

  const filteredEstablishments = getFilteredEstablishments();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Dashboard de Administración
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Vista general de la plataforma
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isLoadingStats || isLoadingEstablishments}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <FiRefreshCw className={`h-4 w-4 ${(isLoadingStats || isLoadingEstablishments) ? 'animate-spin' : ''}`} />
            <span>Actualizar</span>
          </button>
          <button
            onClick={handleCreateEstablishment}
            className="btn btn-primary flex items-center space-x-2"
          >
            <FiPlus className="h-4 w-4" />
            <span>Nuevo Establecimiento</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <AdminStatisticsCards
        stats={platformStats}
        isLoading={isLoadingStats}
      />

      {/* Establishments Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Gestión de Establecimientos
          </h2>
          <span className="text-sm text-gray-600">
            {filteredEstablishments.length} establecimientos
          </span>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
            {/* Search */}
            <div className="relative col-span-2">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Buscar establecimientos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>

            {/* Status filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="">Todos los estados</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Mostrando {filteredEstablishments.length} de {establishments.length} establecimientos
            </p>

            {(searchTerm || statusFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
                className="btn btn-secondary btn-sm"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Establishments Table */}
        {isLoadingEstablishments ? (
          <div className="flex items-center justify-center min-h-96 bg-white rounded-lg shadow">
            <LoadingSpinner size="lg" text="Cargando establecimientos..." />
          </div>
        ) : (
          <EstablishmentTable
            establishments={filteredEstablishments}
            onEdit={handleEditEstablishment}
            onDelete={handleDeleteEstablishment}
            onRefresh={fetchEstablishments}
          />
        )}
      </div>

      {/* Establishment Form Modal */}
      {showEstablishmentForm && (
        <EstablishmentForm
          establishment={selectedEstablishment}
          onSave={handleEstablishmentSaved}
          onClose={() => {
            setShowEstablishmentForm(false);
            setSelectedEstablishment(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Establecimiento"
          message={`¿Estás seguro de que deseas eliminar el establecimiento "${deleteConfirm.name}"? Esta acción eliminará también todos los datos relacionados y no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteEstablishment}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default PlatformAdminDashboard;
